import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "flex flex-col" }
const _hoisted_2 = { class: "font-medium text-sm text-gray-900" }
const _hoisted_3 = { class: "text-sm font-regular text-gray-500" }
const _hoisted_4 = { class: "flex flex-col items-start" }
const _hoisted_5 = { class: "font-normal text-sm text-gray-900" }
const _hoisted_6 = { class: "font-normal text-sm text-gray-900" }
const _hoisted_7 = {
  key: 0,
  class: "inline-flex justify-between"
}
const _hoisted_8 = { class: "flex flex-row items-center justify-end" }

import { computed, onBeforeMount, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BaseIcon } from '@/lib/components/Icon';
import { ButtonLink } from '@/lib/components/Button';
import { DataTable } from '@/lib/components/DataTable';
import { BaseTooltip } from '@/lib/components/Tooltip';
import { RowPatientSummary } from '@/lib/components/PatientSummary';
import {
  CopdHomeMonitoringProgramRow,
  CopdProgram,
  CopdProgramMeasurement,
  CopdProgramReview,
  CopdRelationship,
  Organisation,
  Patient,
  Tag,
  TagGroup
} from '@/models';
import kebabCase from 'lodash-es/kebabCase';
import { FEATURES } from '@/constants';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { IColumn } from '@/lib';


export default /*#__PURE__*/_defineComponent({
  __name: 'CopdPatientsTable',
  props: {
  loading: {
    type: Boolean,
    required: true
  },
  organisationId: {
    type: String,
    required: true
  },
  sort: {
    type: String,
    required: true
  },
  page: {
    type: Number,
    required: true
  },
  rows: {
    type: Array as PropType<CopdProgram[]>,
    default: () => []
  },
  tags: {
    type: Array as PropType<Array<Tag>>,
    default: () => []
  },
  tagGroups: {
    type: Array as PropType<Array<TagGroup>>,
    default: () => []
  },
  patients: {
    type: Array as PropType<Patient[]>,
    default: () => []
  },
  organisationalUnits: {
    type: Array as PropType<Organisation[]>,
    default: () => []
  },
  reviews: {
    type: Array as PropType<CopdProgramReview[]>,
    default: () => []
  },
  measurements: {
    type: Array as PropType<CopdProgramMeasurement[]>,
    default: () => []
  }
},
  setup(__props) {

const props = __props;

dayjs.extend(utc);

const { t } = useI18n();


const columns = ref<IColumn[]>([]);

const tagFeatureFlagEnabled = computed<boolean>(() => isFeatureFlagEnabled(FEATURES.TAGS));

onBeforeMount(() => {
  columns.value = [
    { name: 'last_sent', label: t('custom.uhb.copd.last-sent') },
    { name: 'patient', label: t('platform.patient.patient') },
    { name: 'status', label: t('platform.common.status') },
    { name: 'actions', label: '', align: 'right' }
  ];
});
onMounted(() => {
  const dynamicCol = tagFeatureFlagEnabled.value
    ? { name: 'team', label: t('custom.uhb.copd.teams') }
    : {
      name: 'organisation',
      label: t('platform.org-unit.org-unit')
    };
  columns.value.splice(2, 0, dynamicCol);
});
const formatDate = (date: string) => dayjs(date).format('D MMM YYYY');
const dischargedAt = (row: CopdHomeMonitoringProgramRow) => {
  const review = reviewItem(row);
  if (review) {
    const date = review.attributes.created_at;
    return formatDate(date);
  }
  return '';
};
const dischargeKey = (row: CopdHomeMonitoringProgramRow) => {
  const review = reviewItem(row);
  if (review?.attributes.outcome === 'discharge') {
    return kebabCase(review?.attributes.reason as unknown as string);
  }
  return '';
};
const statusColor = (row: CopdHomeMonitoringProgramRow) => {
  switch (status(row)) {
  case 'ready-for-review':
    return row.attributes.high_risk ? 'danger' : 'primary';
  case 'awaiting-measurements':
    return 'gray';
  case 'discharged':
    return 'alert';
  default:
    return 'gray';
  }
};
const patient = (row: CopdHomeMonitoringProgramRow): Patient | undefined => props.patients.find(
  (patient) => patient.id === row.relationships.patient.id
);
const organisationalUnitName = (row: CopdHomeMonitoringProgramRow): string => {
  if (row.relationships.assigned_organisational_unit === null) {
    return '—';
  }
  return (
    props.organisationalUnits.find(
      (organisationalUnit) =>
        organisationalUnit.id ===
        row.relationships.assigned_organisational_unit.id
    )?.name || '—'
  );
};
const getTagName = (row, groupName): string => {
  if (row.relationships.tags === null) {
    return '—';
  }

  let tagName = '-';
  if (groupName === 'community-team') {
    const groups = props.tagGroups.filter((group) => group.attributes.name === groupName);
    tagName = (props.tags.find(
      (tag) =>
        (row.relationships.tags as Array<CopdRelationship>).map((t) => t.id).includes(tag.id)
        && groups && groups.some((group) => tag.relationships?.group?.id === group.id)
    )?.attributes?.name || '—');
  } else {
    const group = props.tagGroups.find((group) => group.attributes.name === groupName);
    tagName = (props.tags.find(
      (tag) =>
        (row.relationships.tags as Array<CopdRelationship>).map((t) => t.id).includes(tag.id)
        && group && tag.relationships?.group?.id === group.id
    )?.attributes?.name || '—');
  }
  return tagName;
};
const lastSentDate = (row: CopdHomeMonitoringProgramRow): string => {
  const measurement: CopdProgramMeasurement | undefined =
    measurementItem(row);
  if (measurement && measurement.attributes) {
    return dayjs
      .utc(measurement.attributes.created_at)
      .local()
      .format('D MMM YYYY');
  }
  return 'Never';
};
const lastSentTime = (row: CopdHomeMonitoringProgramRow): string => {
  const measurement: CopdProgramMeasurement | undefined =
    measurementItem(row);
  if (measurement && measurement.attributes) {
    return dayjs
      .utc(measurement.attributes.created_at)
      .local()
      .format('h:mma');
  }
  return '—';
};
const measurementItem = (
  row: CopdHomeMonitoringProgramRow
): CopdProgramMeasurement | undefined => props.measurements.find(
  (measurement) =>
    measurement.id === row.relationships.latest_measurement?.id
);
const reviewItem = (row: CopdHomeMonitoringProgramRow): CopdProgramReview | undefined => props.reviews.find(
  (review) => review.id === row.relationships.discharged_review?.id
);
const status = (row: CopdHomeMonitoringProgramRow) => {
  if (row.relationships.discharged_review) {
    return 'discharged';
  }

  const measurement: CopdProgramMeasurement | undefined =
    measurementItem(row);

  if (
    measurement &&
    measurement.relationships &&
    measurement.relationships.review === null
  ) {
    return 'ready-for-review';
  }

  return 'awaiting-measurements';
};
const programLink = (row: CopdHomeMonitoringProgramRow) => ({
  name: 'patient-virtual-ward',
  params: {
    patientId: row.relationships.patient?.id,
    programId: row.id,
    organisationId: props.organisationId
  }
});

return (_ctx: any,_cache: any) => {
  const _component_MkBadge = _resolveComponent("MkBadge")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(DataTable), {
      loading: __props.loading,
      columns: columns.value,
      rows: __props.rows,
      sort: __props.sort,
      class: "w-full mt-6"
    }, _createSlots({
      "cell.last_sent": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, _toDisplayString(lastSentDate(row)), 1),
          _createElementVNode("div", _hoisted_3, _toDisplayString(lastSentTime(row)), 1)
        ])
      ]),
      "cell.patient": _withCtx(({ row }) => [
        _createVNode(_unref(RowPatientSummary), {
          patient: patient(row),
          "home-monitoring": getTagName(row, 'home-monitoring')
        }, null, 8, ["patient", "home-monitoring"])
      ]),
      "cell.status": _withCtx(({ row }) => [
        _createElementVNode("div", {
          class: _normalizeClass(["flex items-center", statusColor(row) && 'flex-row'])
        }, [
          _createVNode(_component_MkBadge, {
            color: statusColor(row),
            size: "xsmall"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t(`custom.uhb.copd.${status(row)}`)) + " ", 1),
              (status(row) === 'discharged')
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createTextVNode(_toDisplayString(dischargedAt(row)) + " ", 1),
                    _createVNode(_unref(BaseTooltip), null, {
                      target: _withCtx(() => [
                        _createVNode(_unref(BaseIcon), {
                          class: "text-gray-400 hover:text-gray-900 focus:text-gray-900 stroke-2 ml-2",
                          height: "14px",
                          name: "info"
                        })
                      ]),
                      content: _withCtx(() => [
                        _createElementVNode("div", null, _toDisplayString(_ctx.$t(
                        `custom.uhb.copd.discharge-reason.${dischargeKey(row)}`
                      )), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]))
                : (row.attributes.high_risk)
                  ? (_openBlock(), _createBlock(_component_MkBadge, {
                      key: 1,
                      color: "danger",
                      size: "2xsmall",
                      filled: true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('platform.common.high-risk')), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1032, ["color"])
        ], 2)
      ]),
      "cell.actions": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(ButtonLink), {
            to: programLink(row),
            "data-dusk": `view-copd-program-${row.id}-btn`,
            color: "ghost",
            size: "small"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.view')), 1)
            ]),
            _: 2
          }, 1032, ["to", "data-dusk"])
        ])
      ]),
      _: 2
    }, [
      (_unref(tagFeatureFlagEnabled))
        ? {
            name: "cell.team",
            fn: _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, _toDisplayString(getTagName(row, 'hospital-team')), 1),
                _createElementVNode("div", _hoisted_6, _toDisplayString(getTagName(row, 'community-team')), 1)
              ])
            ]),
            key: "0"
          }
        : {
            name: "cell.organisation",
            fn: _withCtx(({ row }) => [
              _createElementVNode("div", { class: "flex flex-col items-start" }, [
                _createElementVNode("div", { class: "font-medium text-sm text-gray-900" }, _toDisplayString(organisationalUnitName(row)), 1)
              ])
            ]),
            key: "1"
          }
    ]), 1032, ["loading", "columns", "rows", "sort"])
  ]))
}
}

})